function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
import clsx from 'clsx';
import { FilterItem } from 'apps/boards/src/dashboards-app/components/global-filter-bar/filter-item';
import { FilterPlaceholder } from 'apps/boards/src/dashboards-app/components/global-filter-bar/filter-placeholder';
import { useTranslation } from 'common/i18n/hooks';
import { ButtonReact } from 'common/components/button';
import { DateRangePickerReact } from 'common/widgets/date-range-picker-new';
import { DatePickerMode } from 'common/widgets/date-range-picker-new/util';
import { DateRangeType, DisplayContext } from 'common/widgets/date-range-picker/models/types';
import { PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS, PresetDateRange } from 'common/widgets/date-range-picker/models/presets';
import { ItemsMenuReact } from 'common/components/items-menu';
import { DateRange } from 'common/widgets/date-range-picker/models/date-range';
import { BoardFilter } from 'apps/boards/src/dashboards-app/components/global-filter-bar/types';
import { ButtonTheme } from 'common/components/button/constants';
import { Placement } from 'common/components/tooltip/types';
import { isEqualToSavedFilters } from 'apps/boards/src/dashboards-app/util';
import { getProjectId, getWorkspaceId } from 'common/report/util';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ItemClickEvent, Section } from 'common/components/items-menu/types';
import { formatDate } from 'common/util/date';
import { TIMESTAMP_FORMAT } from 'common/constants';
import { useCanvasStore } from '../store';
import { TimeRangeAndComparison } from '../types';
import { TimeRangesAndComparisonsMenu } from '../components/time-ranges-and-comparisons-menu';
import styles from './index.module.styl';
export function FilterPanel() {
  var _userCookieUtils$getU, _currentProject$get$v;
  const isViewOnly = useCanvasStore(store => {
    var _store$canvas;
    return !((_store$canvas = store.canvas) !== null && _store$canvas !== void 0 && _store$canvas.can_update_basic);
  });
  const globalFilters = useCanvasStore(store => store.globalFilters);
  const setGlobalFilters = useCanvasStore(store => store.setGlobalFilters);
  const savedGlobalFilters = useCanvasStore(store => store.savedGlobalFilters);
  const setSavedGlobalFilters = useCanvasStore(store => store.setSavedGlobalFilters);
  const asOfDate = useCanvasStore(store => store.asOfDate);
  const setAsOfDate = useCanvasStore(store => store.setAsOfDate);
  const savedAsOfDate = useCanvasStore(store => store.savedAsOfDate);
  const setSavedAsOfDate = useCanvasStore(store => store.setSavedAsOfDate);
  const timeRangesAndComparisons = useCanvasStore(store => store.timeRangesAndComparisons);
  const setTimeRangesAndComparisons = useCanvasStore(store => store.setTimeRangesAndComparisons);
  const savedTimeRangesAndComparisons = useCanvasStore(store => store.savedTimeRangesAndComparisons);
  const setSavedTimeRangesAndComparisons = useCanvasStore(store => store.setSavedTimeRangesAndComparisons);
  const resetActiveTimeRangeIndex = useCanvasStore(store => store.resetActiveTimeRangeIndex);
  const [asOfDateMenuState, setAsOfDateMenuState] = useState<null | `list` | `custom`>(null);
  const [isTimeRangesMenuOpen, setIsTimeRangesMenuOpen] = useState<boolean>(false);
  const [isFilterPlaceholderOpen, setIsFilterPlaceholderOpen] = useState<boolean>(false);
  const currentProject = useCurrentProject();
  const {
    t,
    print
  } = useTranslation();
  const projectId = Number(getProjectId());
  const workspaceId = Number(getWorkspaceId());
  const userId = (_userCookieUtils$getU = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU === void 0 ? void 0 : _userCookieUtils$getU.id;
  const timezone = (_currentProject$get$v = currentProject.get().value) === null || _currentProject$get$v === void 0 ? void 0 : _currentProject$get$v.timezone;
  const areGlobalOptionsChanged = useMemo(() => !isEqualToSavedFilters(savedGlobalFilters, globalFilters) || !isEqual(savedAsOfDate, asOfDate) || !isEqual(savedTimeRangesAndComparisons, timeRangesAndComparisons), [savedAsOfDate, asOfDate, savedTimeRangesAndComparisons, timeRangesAndComparisons, globalFilters, savedGlobalFilters]);
  const asOfDateCalendarDate = asOfDate === "today" ? new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Today]).getCalendarDatesAsOf(undefined, undefined, timezone, undefined) : {
    from: asOfDate.slice(0, 10),
    to: asOfDate.slice(0, 10)
  };
  const asOfDateStr = asOfDate === "today" ? "Today" : formatDate(new Date(asOfDate));
  const asOfDatePresetOptions: Section[] = [{
    items: [{
      value: "today",
      label: print("As of today"),
      isSelected: true
    }, {
      value: "custom",
      label: print("As of date...")
    }]
  }];
  const timeRangesAndComparisonsStr = timeRangesAndComparisons.map(_ref => {
    let {
      title
    } = _ref;
    return title || "None";
  }).join(", ");
  const handleAsOfPresetClick = (ev?: ItemClickEvent) => {
    var _ev$detail;
    if ((ev === null || ev === void 0 || (_ev$detail = ev.detail) === null || _ev$detail === void 0 || (_ev$detail = _ev$detail.item) === null || _ev$detail === void 0 ? void 0 : _ev$detail.value) === "custom") {
      setAsOfDateMenuState("custom");
    } else {
      setAsOfDate("today");
      setAsOfDateMenuState(null);
    }
  };
  const handleAsOfDateChange = (dateRange: DateRange) => {
    const calendarDate = dateRange.getCalendarDatesAsOf(undefined, TIMESTAMP_FORMAT, undefined, undefined);
    if (calendarDate.to) {
      setAsOfDate(calendarDate.to);
    }
    setAsOfDateMenuState(null);
  };
  const handleTimeRangesAndComparisonsChange = (newTimeRangesAndComparisons: TimeRangeAndComparison[]) => {
    if (newTimeRangesAndComparisons.length < timeRangesAndComparisons.length) {
      // if a set of time range and comparison is removed, reset active time range index for all the metric nodes
      resetActiveTimeRangeIndex();
    }
    setTimeRangesAndComparisons(newTimeRangesAndComparisons);
  };
  const handleChangeGlobalFilters = (newGlobalFilters: BoardFilter[]) => {
    setGlobalFilters(newGlobalFilters);
  };
  const handleDiscardGlobalOptions = () => {
    // reset current options back to saved options
    setAsOfDate(savedAsOfDate);
    setTimeRangesAndComparisons(savedTimeRangesAndComparisons);
    setGlobalFilters(savedGlobalFilters);
  };
  const handleSaveGlobalOptions = () => {
    // sync saved options to current options
    setSavedAsOfDate(asOfDate);
    setSavedTimeRangesAndComparisons(timeRangesAndComparisons);
    setSavedGlobalFilters(globalFilters);
  };
  return <div className={styles.filterPanel} data-sentry-component="FilterPanel" data-sentry-source-file="filter-panel.tsx">
      <SelectReact theme={SelectTheme.Tertiary} className={clsx(styles.asOfFilterSelect, {
      [styles.open]: asOfDateMenuState !== null
    })} alwaysShowCaret label={"As of"} open={asOfDateMenuState !== null} onMenuChange={ev => setAsOfDateMenuState(ev.detail.isOpen ? "list" : null)} tooltip={{
      text: "The end date for time ranges",
      placement: Placement.Bottom
    }} data-sentry-element="SelectReact" data-sentry-source-file="filter-panel.tsx">
        <div slot='button-suffix' className={styles.buttonSuffix}>
          &nbsp;&nbsp;{asOfDateStr}
        </div>
        <div slot='body'>
          {asOfDateMenuState === "custom" && <DateRangePickerReact datePickerModesOptions={[DatePickerMode.On]} disableSubDayGranularityMessage='Sub-day granularity is not supported in canvas' displayContext={DisplayContext.Default} dateRange={_objectSpread({
          type: DateRangeType.On
        }, asOfDateCalendarDate)} isTimePickerEnabled={false} singlePickerMode forceHidePresetMenu onApply={ev => handleAsOfDateChange(ev.detail.dateRange)} onCancel={() => setAsOfDateMenuState(null)} />}
          {asOfDateMenuState === "list" && <ItemsMenuReact style={{
          width: "150px"
        }} sections={asOfDatePresetOptions} onClickItem={handleAsOfPresetClick} />}
        </div>
      </SelectReact>
      <SelectReact theme={SelectTheme.Tertiary} className={clsx(styles.timeRangesAndComparisonsSelect, {
      [styles.open]: isTimeRangesMenuOpen
    })} alwaysShowCaret label={"Time Ranges"} onMenuChange={ev => setIsTimeRangesMenuOpen(ev.detail.isOpen)} open={isTimeRangesMenuOpen} data-sentry-element="SelectReact" data-sentry-source-file="filter-panel.tsx">
        <div slot='button-suffix' className={styles.buttonSuffix}>
          &nbsp;&nbsp;{timeRangesAndComparisonsStr}
        </div>
        <div slot='body'>
          {isTimeRangesMenuOpen && <TimeRangesAndComparisonsMenu timeRangesAndComparisons={timeRangesAndComparisons} asOfDate={asOfDate} onTimeRangeAndComparisonChange={handleTimeRangesAndComparisonsChange} />}
        </div>
      </SelectReact>
      {globalFilters.map((filter, index) => <FilterItem key={"filter-".concat(index)} filter={filter} onChange={newFilter => {
      const newFilters = [...globalFilters];
      newFilters[index] = newFilter;
      handleChangeGlobalFilters(newFilters);
    }} onClickDelete={() => {
      const newFilters = [...globalFilters];
      newFilters.splice(index, 1);
      handleChangeGlobalFilters(newFilters);
    }} onClickDuplicate={() => {
      const newFilters = [...globalFilters.slice(0, index), _objectSpread({}, filter), ...globalFilters.slice(index)];
      handleChangeGlobalFilters(newFilters);
    }} projectId={projectId} workspaceId={workspaceId} userId={userId as number} trackName={"canvas"} onTrack={() => null} />)}
      {isFilterPlaceholderOpen && <FilterPlaceholder onAdd={filter => handleChangeGlobalFilters([...globalFilters, filter])} onClose={() => setIsFilterPlaceholderOpen(false)} projectId={projectId} workspaceId={workspaceId} userId={userId as number} trackName={"canvas"} onTrack={() => null} />}
      <ButtonReact theme={ButtonTheme.Tertiary} icon='plus' onClick={() => setIsFilterPlaceholderOpen(true)} data-sentry-element="ButtonReact" data-sentry-source-file="filter-panel.tsx">
        {t("canvases.actions.filter")}
      </ButtonReact>
      {globalFilters.length !== 0 && <span className={styles.divider} />}
      {areGlobalOptionsChanged && <div className={clsx(styles.saveButtonContainer, {
      [styles.empty]: globalFilters.length === 0
    })}>
          <ButtonReact theme={ButtonTheme.Tertiary} onClick={handleDiscardGlobalOptions}>
            {isViewOnly ? t("common.reset") : t("common.cancel")}
          </ButtonReact>
          {!isViewOnly && <ButtonReact theme={ButtonTheme.Primary} tooltip={{
        text: t("canvases.tooltip.save-to-canvas"),
        placement: Placement.Bottom
      }} onClick={handleSaveGlobalOptions}>
              {t("common.save")}
            </ButtonReact>}
        </div>}
    </div>;
}